import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { AnalyticsEventRequest, createAnalyticsEvent, ProductAnalyticsEventIdentity } from '@/api/analytics.ts';
import { useWhoamiStore } from '@/store/useWhoamiStore.ts';

interface EventProperties {
	[key: string]: unknown;
}

export type ProductAnalyticsEventOrigin =
	| { area?: never; section?: never; component?: never }
	| { area: string; section?: never; component?: never }
	| { area: string; section: string; component?: never }
	| { area: string; section: string; component: string };

interface TrackEventOptions {
	origin?: ProductAnalyticsEventOrigin;
	postHogEventName?: string;
	postHogProperties?: EventProperties;
	deduplicationKey?: string;
}

interface AnalyticsService {
	trackEvent: (eventName: string, properties?: EventProperties) => void;
}

const eventDeduplicationWindowMs = 1_000;
const lastCapturedAtByEvent = new Map<string, number>();

const postHogAnalytics: AnalyticsService = {
	trackEvent: (eventName: string, properties: EventProperties = {}) => {
		const { user } = useWhoamiStore.getState();

		const eventProps: EventProperties = {
			...properties,
			...(user && {
				user_id: user.id?.toString(),
				email: user.email,
				name: `${user.firstName} ${user.lastName}`,
				role: user.spotnanaRole,
				organization_id: user.organization?.id?.toString(),
				organization_name: user.organization?.name,
			}),
		};

		posthog.capture(eventName, eventProps);
	},
};

export const trackPostHogEvent = (eventName: string, properties?: EventProperties): void => {
	postHogAnalytics.trackEvent(eventName, properties);
};

let hasReportedAnalyticsDeliveryFailure = false;

const reportAnalyticsDeliveryFailure = (error: unknown) => {
	if (import.meta.env.DEV) {
		console.warn('Failed to deliver product analytics event.', error);
	}

	if (hasReportedAnalyticsDeliveryFailure) return;

	hasReportedAnalyticsDeliveryFailure = true;
	Sentry.captureException(error, {
		tags: {
			feature: 'product-analytics-delivery',
		},
	});
};

export const trackEvent = (event: ProductAnalyticsEventIdentity, options: TrackEventOptions = {}): void => {
	const eventKey = [event.category, event.feature, event.subfeature, event.action, options.deduplicationKey]
		.filter(Boolean)
		.join('.');
	const capturedAt = Date.now();
	const lastCapturedAt = lastCapturedAtByEvent.get(eventKey);

	if (lastCapturedAt !== undefined && capturedAt - lastCapturedAt < eventDeduplicationWindowMs) {
		return;
	}

	lastCapturedAtByEvent.set(eventKey, capturedAt);
	setTimeout(() => {
		if (lastCapturedAtByEvent.get(eventKey) === capturedAt) {
			lastCapturedAtByEvent.delete(eventKey);
		}
	}, eventDeduplicationWindowMs);

	if (options.postHogEventName) {
		trackPostHogEvent(options.postHogEventName, options.postHogProperties);
	}

	const request: AnalyticsEventRequest = {
		...event,
		...options.origin,
	};

	void createAnalyticsEvent(request).catch(reportAnalyticsDeliveryFailure);
};
